import type { ChatParticipant, ParticipantStatus } from '@localagent/sdk';

export interface ChatTab {
  participantId: string;
  label: string;
  status: ParticipantStatus;
  background: boolean;
  ownerChain: string[];
}

export interface ParticipantTabsUrlState {
  openIds: string[];
  selectedId: string | null;
}

const TABS_PARAM = 'tabs';
const TAB_PARAM = 'tab';

export function readParticipantTabs(params: URLSearchParams): ParticipantTabsUrlState {
  const listed = (params.get(TABS_PARAM) ?? '').split(',').filter((id) => id.length > 0);
  const selectedId = params.get(TAB_PARAM) || null;
  const openIds = selectedId !== null && !listed.includes(selectedId) ? [...listed, selectedId] : listed;
  return { openIds, selectedId };
}

export function writeParticipantTabs(next: URLSearchParams, state: ParticipantTabsUrlState): void {
  if (state.openIds.length > 0) next.set(TABS_PARAM, state.openIds.join(','));
  else next.delete(TABS_PARAM);
  if (state.selectedId !== null) next.set(TAB_PARAM, state.selectedId);
  else next.delete(TAB_PARAM);
}

export const MAIN_TAB_LABEL = 'Main';
export const QUESTIONS_TAB_ID = 'questions';

// (app-shell ADR-006)
export interface ParticipantTabsControl extends ParticipantTabsUrlState {
  onOpen: (participantId: string) => void;
  onSelect: (participantId: string | null) => void;
  onClose: (participantId: string) => void;
}

export function buildChatTabs(
  participants: readonly ChatParticipant[],
  openIds: readonly string[],
  spawnLabel: (spawnTaskId: string) => string | undefined,
): ChatTab[] {
  const byId = new Map(participants.map((p) => [p.id, p]));
  const tabs: ChatTab[] = [];
  for (const id of openIds) {
    const participant = byId.get(id);
    if (!participant || participant.isPrimary) continue;
    tabs.push({
      participantId: id,
      label: participant.label,
      status: participant.status,
      background: participant.background,
      ownerChain: ownerChain(participant, byId, spawnLabel),
    });
  }
  return tabs;
}

function ownerChain(
  participant: ChatParticipant,
  byId: ReadonlyMap<string, ChatParticipant>,
  spawnLabel: (spawnTaskId: string) => string | undefined,
): string[] {
  const chain: string[] = [];
  const seen = new Set<string>();
  let current: ChatParticipant | undefined = participant;
  while (current && !current.isPrimary && !seen.has(current.id)) {
    seen.add(current.id);
    chain.unshift(current.label);
    const via = current.spawnTaskId !== null ? spawnLabel(current.spawnTaskId) : undefined;
    if (via) chain.unshift(via);
    current = current.ownerParticipantId !== null ? byId.get(current.ownerParticipantId) : undefined;
  }
  chain.unshift(MAIN_TAB_LABEL);
  return chain;
}
